import { useRender } from "@base-ui/react/use-render";
import chevronRightIcon from "@loot-os/icons/icons/chevron-right";
import type { ReactElement, ReactNode } from "react";
import { Icon } from "../core/Icon.js";
import { Text } from "../core/Text.js";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";

export interface SectionHeaderProps {
  title: string;
  /** Give the heading an id so the section can be named by it. */
  titleId?: string;
  /** One sentence saying what the section promises. */
  subtitle?: string;
  /** The heading level from the page outline. */
  level?: "h1" | "h2" | "h3";
  /** "View all packs ›": the link names its destination. */
  action?: { label: string; href?: string; render?: ReactElement };
  /** Controls under the title row: filter chips, a sort. */
  children?: ReactNode;
  /** `lg`: a page's section title. `sm`: a quieter title for a dense page of rails. */
  size?: "lg" | "sm";
  className?: string;
}

function ActionLink({ label, href, render }: NonNullable<SectionHeaderProps["action"]>) {
  return useRender({
    render: useLinkRender(render),
    defaultTagName: "a",
    props: {
      href,
      className: cn(
        "inline-flex shrink-0 items-center gap-1 rounded-xs font-body text-body-b2-strong text-accent-text hover:text-text-primary",
        FOCUS_RING,
      ),
      children: (
        <>
          {label}
          <Icon icon={chevronRightIcon} />
        </>
      ),
    },
  });
}

/** The top of a content section: title, a one-line promise, a "view all" link, and filters. */
export function SectionHeader({
  title,
  titleId,
  subtitle,
  level = "h2",
  action,
  children,
  size = "lg",
  className,
}: SectionHeaderProps) {
  return (
    <header className={cn("flex flex-col gap-5", className)}>
      <div className="flex items-end justify-between gap-6">
        <div className="flex flex-col gap-1">
          <Text
            as={level}
            id={titleId}
            variant={size === "sm" ? "display-xl3" : "display-xl2"}
            tone="primary"
            className={size === "sm" ? "max-md:text-display-xl4" : "max-md:text-display-xl3"}
          >
            {title}
          </Text>
          {subtitle && (
            <Text variant="body-b1" tone="tertiary">
              {subtitle}
            </Text>
          )}
        </div>
        {action && <ActionLink {...action} />}
      </div>
      {children}
    </header>
  );
}
